// Birthdays, saved places and situations, as stored in `birthdays/{id}`, `places/{id}` and `situations/{id}`
// (Services/Birthdays.swift: Birthday; Services/Situations.swift: SavedPlace, TimeWindow, SituationTrigger,
// SituationScope, Situation). The web shows and keeps them; it applies no situation (docs/47).
//
// Triggers and scopes are enums a newer version may add cases to: one this version doesn't know is kept as stored
// (`{kind: "unknown", raw}`) and written back unchanged, as the kit's `StoredValue` does.

import type { Weekday } from "../types";
import { readRecord } from "./shared";
import {
  associatedValue, decoding, document, enumCase, readEnum, readInt, readStruct, readUUID, readString, storedFields,
  withoutUndefined, DecodeFailure,
  type DecodeContext, type Decoded, type EncodeOptions, type Fields, type ValueReader,
} from "./values";

// MARK: - Birthday

export interface Birthday {
  id: string;
  name: string;
  month: number;
  day: number;
  /** The year of birth, when known. */
  year?: number;
  source: "todolix" | "contacts";
  /** The year a to-do to congratulate was last made. */
  congratulatedYear?: number;
  isSample?: boolean;
  /** False when this birthday makes no to-do on the day. */
  makesTodo?: boolean;
  /** From Contacts, changed in Todolix: reading Contacts leaves it alone. */
  isEdited?: boolean;
  isHidden?: boolean;
  unknownFields: Fields;
}

export const BIRTHDAY_FIELDS = [
  "id", "name", "month", "day", "year", "source", "congratulatedYear", "isSample", "makesTodo", "isEdited", "isHidden",
] as const;

export function decodeBirthday(data: Fields, context: DecodeContext): Decoded<Birthday> {
  return decoding(() => readRecord(data, context, (fields) => withoutUndefined<Birthday>({
    id: fields.uuid("id"),
    name: fields.string("name"),
    month: fields.int("month"),
    day: fields.int("day"),
    year: fields.optionalInt("year"),
    source: fields.required("source", readEnum(["todolix", "contacts"] as const)),
    congratulatedYear: fields.optionalInt("congratulatedYear"),
    isSample: fields.optionalBool("isSample"),
    makesTodo: fields.optionalBool("makesTodo"),
    isEdited: fields.optionalBool("isEdited"),
    isHidden: fields.optionalBool("isHidden"),
    unknownFields: fields.unknownFields(BIRTHDAY_FIELDS),
  })));
}

export function encodeBirthday(birthday: Birthday, options?: EncodeOptions): Fields {
  return storedFields(document(birthday.unknownFields, {
    id: birthday.id, name: birthday.name, month: birthday.month, day: birthday.day, year: birthday.year,
    source: birthday.source, congratulatedYear: birthday.congratulatedYear, isSample: birthday.isSample,
    makesTodo: birthday.makesTodo, isEdited: birthday.isEdited, isHidden: birthday.isHidden,
  }), options);
}

// MARK: - SavedPlace

/** A place the user named once, like Home or Work, for situations and place reminders to refer to. */
export interface Place {
  id: string;
  name: string;
  kind: "home" | "work" | "other";
  latitude: number;
  longitude: number;
  /** Metres around the point that still count as being there. */
  radius: number;
  unknownFields: Fields;
}

export const PLACE_FIELDS = ["id", "name", "kind", "latitude", "longitude", "radius"] as const;

export function decodePlace(data: Fields, context: DecodeContext): Decoded<Place> {
  return decoding(() => readRecord(data, context, (fields) => ({
    id: fields.uuid("id"),
    name: fields.string("name"),
    kind: fields.required("kind", readEnum(["home", "work", "other"] as const)),
    latitude: fields.number("latitude"),
    longitude: fields.number("longitude"),
    radius: fields.number("radius"),
    unknownFields: fields.unknownFields(PLACE_FIELDS),
  })));
}

export function encodePlace(place: Place, options?: EncodeOptions): Fields {
  return storedFields(document(place.unknownFields, {
    id: place.id, name: place.name, kind: place.kind, latitude: place.latitude, longitude: place.longitude, radius: place.radius,
  }), options);
}

// MARK: - Situation

/** Days of the week and minutes of the day, e.g. Monday to Friday, 9:00 to 18:00. A window may run past midnight. */
export interface TimeWindow {
  /** 1 = Sunday … 7 = Saturday. A set, stored as an array. */
  weekdays: Weekday[];
  /** Minutes after midnight. */
  from: number;
  to: number;
}

export type SituationTrigger =
  | { kind: "atPlace"; placeID: string }
  | { kind: "inTime"; window: TimeWindow }
  /** A trigger from a newer version: kept as stored; it never applies. */
  | { kind: "unknown"; raw: unknown };

export type SituationScope =
  | { kind: "area"; areaID: string }
  | { kind: "tag"; tag: string }
  /** Something to hide from a newer version: kept as stored; it hides nothing. */
  | { kind: "unknown"; raw: unknown };

export interface Situation {
  id: string;
  name: string;
  trigger: SituationTrigger;
  hides: SituationScope[];
  isEnabled: boolean;
  unknownFields: Fields;
}

export const SITUATION_FIELDS = ["id", "name", "trigger", "hides", "isEnabled"] as const;

const readTimeWindow: ValueReader<TimeWindow> = readStruct((fields) => ({
  weekdays: [...new Set(fields.required("weekdays", (value, path, context) => {
    if (!Array.isArray(value)) throw new DecodeFailure(`${path} has the wrong shape`);
    return value.map((item, index) => readInt(item, `${path}[${index}]`, context) as Weekday);
  }))],
  from: fields.int("from"),
  to: fields.int("to"),
}));

/** The known cases first, as the kit tries them; anything else is kept as it came. */
const readTrigger: ValueReader<SituationTrigger> = (value, path, context) => {
  try {
    const found = enumCase(value, ["atPlace", "inTime"] as const);
    if (found?.name === "atPlace") return { kind: "atPlace", placeID: associatedValue(found.payload, readUUID, path, context) };
    if (found?.name === "inTime") return { kind: "inTime", window: associatedValue(found.payload, readTimeWindow, path, context) };
  } catch (error) {
    if (!(error instanceof DecodeFailure)) throw error;
  }
  return { kind: "unknown", raw: value };
};

const readScope: ValueReader<SituationScope> = (value, path, context) => {
  try {
    const found = enumCase(value, ["area", "tag"] as const);
    if (found?.name === "area") return { kind: "area", areaID: associatedValue(found.payload, readUUID, path, context) };
    if (found?.name === "tag") return { kind: "tag", tag: associatedValue(found.payload, readString, path, context) };
  } catch (error) {
    if (!(error instanceof DecodeFailure)) throw error;
  }
  return { kind: "unknown", raw: value };
};

function wrongShapeAt(path: string): never {
  throw new DecodeFailure(`${path} has the wrong shape`);
}

export function encodeTrigger(trigger: SituationTrigger): unknown {
  switch (trigger.kind) {
    case "atPlace": return { atPlace: { _0: trigger.placeID } };
    case "inTime": return { inTime: { _0: { weekdays: [...trigger.window.weekdays], from: trigger.window.from, to: trigger.window.to } } };
    case "unknown": return trigger.raw;
  }
}

export function encodeScope(scope: SituationScope): unknown {
  switch (scope.kind) {
    case "area": return { area: { _0: scope.areaID } };
    case "tag": return { tag: { _0: scope.tag } };
    case "unknown": return scope.raw;
  }
}

export function decodeSituation(data: Fields, context: DecodeContext): Decoded<Situation> {
  return decoding(() => readRecord(data, context, (fields) => ({
    id: fields.uuid("id"),
    name: fields.string("name"),
    trigger: fields.required("trigger", readTrigger),
    hides: fields.required("hides", (value, path, ctx) => {
      if (!Array.isArray(value)) throw new DecodeFailure(`${path} has the wrong shape`);
      // A null in the list is refused, as Swift's decoder refuses it for a non-optional element.
      return value.map((item, index) => (item === null ? wrongShapeAt(`${path}[${index}]`) : readScope(item, `${path}[${index}]`, ctx)));
    }),
    isEnabled: fields.bool("isEnabled"),
    unknownFields: fields.unknownFields(SITUATION_FIELDS),
  })));
}

export function encodeSituation(situation: Situation, options?: EncodeOptions): Fields {
  return storedFields(document(situation.unknownFields, {
    id: situation.id,
    name: situation.name,
    trigger: encodeTrigger(situation.trigger),
    hides: situation.hides.map(encodeScope),
    isEnabled: situation.isEnabled,
  }), options);
}
